/* eslint-disable react/display-name */
import type { MouseEvent, ReactElement, ReactNode } from "react"

import { QueryClient, type QueryKey, useQueryClient } from "@tanstack/react-query"
import { m } from "framer-motion"
import eq from "lodash-es/eq"
import includes from "lodash-es/includes"
import lowerCase from "lodash-es/lowerCase"
import { Duration } from "luxon"
import { Link, type Params, useParams } from "react-router-dom"
import ReactTooltip from "react-tooltip"

import { ArrowIcon, HelpIcon } from "@/3514/assets"
import { type TAccordionRenderFunction } from "@/3514/components/shared/accordion/accordion.types"
import { Avatar } from "@/3514/components/shared/avatar/avatar.component"
import { EAvatarSize } from "@/3514/components/shared/avatar/avatar.types"
import { Typography } from "@/3514/components/shared/typography/typography.component"
import {
    ETypographyAlignment,
    ETypographyColor,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight
} from "@/3514/components/shared/typography/typography.types"
import { sessionStatusTranslationsMap } from "@/3514/config"
import { EPath } from "@/3514/constants"
import type { TCoachingSessionsModalSimpleProps as TModalProps } from "@/3514/entities/coaching-sessions"
import {
    type TScreenBreakpoints,
    type TUseInteractionState,
    useInteractionState,
    useIsNetworkFast,
    useScreenBreakpoints
} from "@/3514/hooks"
import {
    EAttendanceModalOrigin,
    type TAttendanceModalProps,
    attendanceMarksQueryPrefetcher,
    isEnoughTimeToMarkAttendance
} from "@/3514/modals/attendance"
import { EPostSessionModalMode, type TPostSessionModalProps } from "@/3514/modals/post-session"
import { coachRatingQueryPrefetcher } from "@/3514/queries"
import { EModalId, type TModalDispatcher, useModalDispatcher } from "@/3514/store/modal"
import { THEME } from "@/3514/theme"
import {
    ESessionAttendanceStatus,
    ESessionModality,
    EUserRole,
    type IUserCoachModel,
    type IUserParticipantModel
} from "@/3514/types"
import { ERoundDirection, withPressHandler } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"
import { type TEmptyAsyncCallback, type TEmptyRenderCallback, emptyRenderCallback } from "@/shared/types/functions"

import assets from "./session-info.assets"
import Dropdown from "./session-info.component.dropdown"
import classes from "./session-info.styles"
import type {
    TSessionInfoHeaderSessionData as THeaderSessionData,
    TSessionInfoRenderActionsPropsFunction as TRenderActionsPropsFunction,
    _TSessionInfoRenderFunctionSharedInternalParams as TRenderFunctionInternalParams,
    TSessionInfoRenderHeaderIconFunction as TRenderHeaderIconFunction,
    TSessionInfoRenderNoticeFunction as TRenderTimeRemarkFunction,
    TSessionInfoParticipant
} from "./session-info.types"

function renderSessionInfoHeaderAvatar(
    modality: ESessionModality,
    options?: { isGuest?: boolean; isGifted?: boolean; size?: 40 | 45 }
): TEmptyRenderCallback {
    const size: 40 | 45 = options?.size ?? 45

    function Wrapper({ children }: { children: ReactNode }): ReactElement {
        return (
            <div
                className={classes.headerAvatar.wrapper()}
                style={{ minWidth: `${size}px`, width: `${size}px`, height: `${size}px` }}
            >
                {options.isGifted && <assets.GiftIcon className={classes.headerAvatar.giftIcon()} />}

                {children}
            </div>
        )
    }

    if (options.isGuest)
        return (): ReactElement => (
            <Wrapper>
                <assets.ExtraPartyIcon />
            </Wrapper>
        )

    if (modality === ESessionModality.Individual)
        return (): ReactElement => (
            <Wrapper>
                <assets.IndividualAvatarIcon />
            </Wrapper>
        )

    return (): ReactElement => (
        <Wrapper>
            <assets.GroupAvatarIcon />
        </Wrapper>
    )
}

function renderSessionInfoHeaderChevron(params: { isSessionInfoExpandable: boolean }): TRenderHeaderIconFunction {
    return function (isAccordionExpanded: boolean, helpers?: { toggle: () => void }): ReactElement {
        const { isActionAffected, actions }: TUseInteractionState = useInteractionState()
        const { t }: IUseTranslation = useTranslation()

        return params.isSessionInfoExpandable ? (
            <button
                type="button"
                className={classes.arrowIcon.container()}
                aria-label={
                    isAccordionExpanded
                        ? t("entities.sessions.accordion.chevron.expanded.ariaLabel")
                        : t("entities.sessions.accordion.chevron.collapsed.ariaLabel")
                }
                onMouseMove={actions.onMouseMove}
                onMouseEnter={actions.onMouseEnter}
                onMouseLeave={actions.onMouseLeave}
                onClick={(event: MouseEvent): void => {
                    actions.onClick()
                    event.preventDefault()
                    helpers?.toggle?.()
                }}
            >
                <ArrowIcon
                    width={15}
                    height={15}
                    fillColor={isActionAffected ? THEME.COLORS.accent : THEME.COLORS.blue[200]}
                    direction={isAccordionExpanded ? ERoundDirection.Top : ERoundDirection.Bottom}
                    className={classes.arrowIcon.arrow()}
                />
            </button>
        ) : null
    }
}

function renderSessionInfoAttendanceListItem({
    viewerRole,
    participant,
    groupId,
    modality
}: {
    viewerRole: EUserRole
    participant: TSessionInfoParticipant
    groupId: number
    modality: ESessionModality
}): TAccordionRenderFunction {
    const _isCoach: boolean = includes<EUserRole>([EUserRole.Coach, EUserRole.Manager], viewerRole)

    function BaseWrapper({ children }: { children: ReactNode }): ReactElement {
        return (
            <div role="listitem" className={classes.attendee.container({ isAccessible: false })}>
                {children}
            </div>
        )
    }

    function Wrapper({
        children,
        isAccordionExpanded,
        isSameParticipant
    }: {
        children: ReactNode
        isAccordionExpanded: boolean
        isSameParticipant: boolean
    }): ReactElement {
        return _isCoach ? (
            Boolean(participant.isExtraParty || participant.isHost || isSameParticipant) ? (
                <BaseWrapper>{children}</BaseWrapper>
            ) : (
                <Link
                    role="listitem"
                    data-index={participant.id}
                    data-modality={modality}
                    to={((): string => {
                        if (modality === ESessionModality.Individual)
                            return EPath.Coach_IndividualParticipant(
                                { participantId: participant.id },
                                { tab: "Sessions" }
                            )

                        return EPath.Coach_GroupParticipant(
                            { groupId, participantId: participant.id },
                            { tab: "Sessions" }
                        )
                    })()}
                    {...(isAccordionExpanded && {})}
                    tabIndex={isAccordionExpanded ? 0 : -1}
                    className={classes.attendee.container({ isAccessible: true })}
                >
                    {children}
                </Link>
            )
        ) : (
            <BaseWrapper>{children}</BaseWrapper>
        )
    }

    return (isAccordionExpanded: boolean): ReactElement => {
        const { t }: IUseTranslation = useTranslation()

        const { participantId: participantIdParam }: Readonly<Params> = useParams()

        return (
            <Wrapper
                isAccordionExpanded={isAccordionExpanded}
                isSameParticipant={participant.id === Number(participantIdParam)}
            >
                <Avatar
                    isLettered
                    size={EAvatarSize._40}
                    url={participant.photo}
                    userData={{ firstName: participant.firstName, lastName: participant.lastName }}
                    {...(Boolean(!participant.isHost && participant.attendanceStatus !== null && _isCoach) && {
                        renderIcon: (): ReactElement =>
                            participant.attendanceStatus === ESessionAttendanceStatus.Present ? (
                                <assets.PresentAttendanceIcon />
                            ) : (
                                <assets.MissedAttendanceIcon />
                            )
                    })}
                />

                <div>
                    <Typography
                        text={((): string => {
                            if (participant.isExtraParty) if (!participant.firstName) return participant.extraPartyRole

                            return participant.firstName
                        })()}
                        alignment={ETypographyAlignment.Center}
                        className={classes.attendee.name()}
                        color={ETypographyColor.Black}
                        size={ETypographySize.Small}
                    />

                    <Typography
                        text={lowerCase(
                            participant.isHost
                                ? t("common.host")
                                : participant.attendanceStatus === ESessionAttendanceStatus.NotPresent
                                  ? String("")
                                  : t(sessionStatusTranslationsMap[participant.rsvpStatus])
                        )}
                        alignment={ETypographyAlignment.Center}
                        color={ETypographyColor.Gray23}
                        size={ETypographySize.Tiny}
                        isItalic
                    />
                </div>
            </Wrapper>
        )
    }
}

function renderSessionInfoCancelledSessionModalOpener(params: {
    session: THeaderSessionData
    sessionsQueryKey: QueryKey
}): TEmptyRenderCallback {
    return (): ReactElement => {
        const { t }: IUseTranslation = useTranslation()

        const modalDispatcher: TModalDispatcher = useModalDispatcher()

        const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

        function action(): void {
            modalDispatcher.showModal<TModalProps>(EModalId.CancelledSession, {
                sessionId: params.session.sessionId,
                sessionsQueryKey: params.sessionsQueryKey
            })
        }

        return !params.session.isSessionCancelled ? null : (
            <button
                aria-label={t("entities.sessions.modal.cancelled.opener.ariaLabel")}
                tabIndex={0}
                onClick={withPressHandler(action)}
                onKeyDown={withPressHandler(action)}
                className={classes.cancelledSessionModalOpener.button()}
            >
                <Typography
                    text={t("common.cancelled")}
                    color={ETypographyColor.Gray23}
                    size={breakpoints.xs || breakpoints.sm ? ETypographySize.Tiny : ETypographySize.Small}
                    isItalic
                />

                <HelpIcon />
            </button>
        )
    }
}

function renderSessionInfoPostSessionModalOpener(params: {
    isEnabled: boolean
    session: THeaderSessionData
    sessionsQueryKey: QueryKey
    user: IUserCoachModel | IUserParticipantModel
}): TEmptyRenderCallback {
    return (): ReactElement => {
        const { t }: IUseTranslation = useTranslation()

        const modalDispatcher: TModalDispatcher = useModalDispatcher()

        const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

        const qc: QueryClient = useQueryClient()

        const {
            actions: { __setter, onMouseLeave, onMouseEnter }
        }: TUseInteractionState = useInteractionState()

        async function handleMouseEnter(): Promise<void> {
            onMouseEnter()

            await coachRatingQueryPrefetcher({ qc }, { participantId: params.user.id })
        }

        async function handleMouseFocus(): Promise<void> {
            await coachRatingQueryPrefetcher({ qc }, { participantId: params.user.id })
        }

        const handleAction: ReturnType<typeof withPressHandler> = withPressHandler((): void => {
            __setter(true)
            modalDispatcher.showModal<TPostSessionModalProps>(EModalId.PostSession, {
                sessionId: params.session.sessionId,
                moduleId: params.session.moduleId,
                moduleTitle: params.session.moduleTitle,
                mode: EPostSessionModalMode.ManualFeedback,
                sessionsQueryKey: params.sessionsQueryKey,
                user: params.user as IUserParticipantModel
            })
        })

        return !params.session.isSessionAttendanceMarked || !params.isEnabled ? null : (
            <m.button
                whileFocus={{ scale: 1.05 }}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2, ease: "easeIn" }}
                disabled={params.session.isModuleReflectionPassed}
                aria-disabled={params.session.isModuleReflectionPassed}
                aria-label={t("entities.sessions.buttons.postSession.ariaLabel")}
                tabIndex={params.session.isModuleReflectionPassed ? -1 : 0}
                className={classes.postSessionModalOpener.button({
                    isDisabled: params.session.isModuleReflectionPassed
                })}
                {...(!params.session.isModuleReflectionPassed && {
                    onClick: handleAction,
                    onKeyDown: handleAction,
                    onFocus: handleMouseFocus,
                    onMouseEnter: handleMouseEnter,
                    onMouseLeave
                })}
            >
                <assets.FeedbackIcon />

                {breakpoints.xs || breakpoints.sm || params.session.isModuleReflectionPassed ? null : (
                    <Typography
                        text={t("entities.sessions.buttons.postSession.label")}
                        color={ETypographyColor.White}
                        size={ETypographySize.Tiny}
                        className={classes.postSessionModalOpener.label()}
                    />
                )}
            </m.button>
        )
    }
}

function renderSessionInfoAttendanceModalOpener(params: {
    isEnabled: boolean
    session: {
        id: number
        startDate: string
        modality: ESessionModality
        minHoursToEdit: number
    }
    userId: number
    sessionsQueryKey: QueryKey
    groupId: number | null
}): TEmptyRenderCallback {
    return (): ReactElement => {
        const { t }: IUseTranslation = useTranslation()

        const modalDispatcher: TModalDispatcher = useModalDispatcher()

        const isNetworkFast: boolean = useIsNetworkFast()

        const qc: QueryClient = useQueryClient()

        const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

        const actionHandler: ReturnType<typeof withPressHandler> = withPressHandler((): void =>
            modalDispatcher.showModal<TAttendanceModalProps>(EModalId.Attendance, {
                origin: EAttendanceModalOrigin.CoachingSessions,
                sessionId: params.session.id,
                sessionModality: params.session.modality,
                sessionStartTime: params.session.startDate,
                minHoursToMark: params.session.minHoursToEdit,
                groupId: params.groupId,
                participantId: params.userId
            })
        )

        const focusHandler: TEmptyAsyncCallback = async (): Promise<void> => {
            if (isNetworkFast && isEnoughTimeToMarkAttendance(params.session.startDate, params.session.minHoursToEdit))
                await attendanceMarksQueryPrefetcher({ qc }, { sessionId: params.session.id })
        }

        return !params.isEnabled ? null : (
            <m.button
                key={`attendance-${params.session.id}`}
                disabled={false}
                aria-disabled={false}
                whileFocus={{ scale: 1.05 }}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2, ease: "easeIn" }}
                tabIndex={0}
                aria-label={t("entities.sessions.actions.attendance.ariaLabel")}
                onClick={actionHandler}
                onKeyDown={actionHandler}
                onMouseEnter={focusHandler}
                onFocus={focusHandler}
                className={classes.attendanceModalOpener()}
            >
                <assets.AttendanceIcon />

                {breakpoints.xs || breakpoints.sm ? null : (
                    <Typography
                        text={t("common.attendance")}
                        color={ETypographyColor.Black}
                        size={ETypographySize.Tiny}
                    />
                )}
            </m.button>
        )
    }
}

function renderSessionInfoHeaderDropdown(
    renderProps: TRenderActionsPropsFunction
): (params: TRenderFunctionInternalParams) => ReactElement {
    return (params: TRenderFunctionInternalParams): ReactElement => <Dropdown actions={renderProps(params)} />
}

function renderSessionInfoHeaderNameTooltip(params: { sessionId: number; sessionName: string }): TEmptyRenderCallback {
    return function (): ReactElement {
        return (
            <ReactTooltip
                id={String(params.sessionId)}
                arrowColor="transparent"
                border
                borderColor={THEME.COLORS.blue[500]}
                backgroundColor={THEME.COLORS.white}
                effect="solid"
                offset={{ top: -5 }}
                place="top"
                className={classes.tooltip()}
            >
                <Typography
                    text={params.sessionName}
                    size={ETypographySize.Small}
                    color={ETypographyColor.Black}
                    weight={ETypographyWeight.Bold}
                />
            </ReactTooltip>
        )
    }
}

function renderSessionInfoNotice(userRole: EUserRole): TRenderTimeRemarkFunction {
    if (eq(userRole, EUserRole.Assistant)) return emptyRenderCallback

    return function ({ isLive, isUpcoming, modality, isMissed, minDaysToEdit }): ReactElement {
        const { t }: IUseTranslation = useTranslation()

        return Boolean((isLive || isUpcoming) && !isMissed) ? (
            <div className={classes.timeRemark.container()}>
                <Typography
                    isItalic
                    text={((): string => {
                        if (includes<EUserRole>([EUserRole.Coach, EUserRole.Manager], userRole))
                            return t("entities.sessions.helperHint.coach.attendance")

                        if (modality === ESessionModality.Individual)
                            return t("entities.sessions.helperHint.participant.individual", {
                                hours: Duration.fromObject({ days: minDaysToEdit }).as("hours")
                            })

                        return t("entities.sessions.helperHint.participant.group")
                    })()}
                    alignment={ETypographyAlignment.Center}
                    tag={ETypographyTag.Paragraph}
                    size={ETypographySize.Small}
                    color={ETypographyColor.Gray23}
                    withHyphens
                    isPretty
                />
            </div>
        ) : null
    }
}

export {
    renderSessionInfoHeaderAvatar,
    renderSessionInfoHeaderNameTooltip,
    renderSessionInfoNotice,
    renderSessionInfoPostSessionModalOpener,
    renderSessionInfoHeaderDropdown,
    renderSessionInfoCancelledSessionModalOpener,
    renderSessionInfoHeaderChevron,
    renderSessionInfoAttendanceModalOpener,
    renderSessionInfoAttendanceListItem
}
